import { motion } from "framer-motion";
import { Droplets, ScanLine, Home, Building2, ArrowRight, Activity } from "lucide-react";
import { IMAGES } from "@/data/catalog";

const fade = {
  hidden: { opacity: 0, y: 36 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};

export default function Services({ onBook }) {
  return (
    <section id="services" className="relative bg-[#0B192C] py-20 lg:py-28 overflow-hidden grain" data-testid="services-section">
      <div className="absolute inset-0 dot-grid opacity-60" />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 relative z-10">
        <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="flex items-center gap-4 mb-6">
          <span className="font-mono-med text-sm text-[#14B8A6] border border-[#14B8A6]/40 rounded-full px-3.5 py-1">03</span>
          <span className="text-xs font-semibold tracking-[0.25em] uppercase text-slate-400">What we do</span>
          <span className="h-px flex-1 bg-white/10" />
        </motion.div>

        <motion.h2 variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}
          className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white max-w-3xl leading-tight">
          Complete diagnostics, anchored on a specialty <span className="text-[#D92638]">nobody else in Vizag has.</span>
        </motion.h2>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-5">
          {/* Flagship: Hematopathology - big card */}
          <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}
            className="lg:col-span-7 group relative rounded-[1.75rem] overflow-hidden border border-white/10 min-h-[360px] flex flex-col justify-end hover:-translate-y-1.5 transition-all duration-500" data-testid="service-hematopathology">
            <img src={IMAGES.collection} alt="Hematopathology services" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B192C] via-[#0B192C]/55 to-transparent" />
            <div className="relative p-8">
              <div className="flex items-center gap-2 mb-3">
                <span className="bg-[#D92638] text-white text-[10px] font-bold tracking-widest uppercase px-3 py-1 rounded-full">Flagship Specialty</span>
                <span className="font-mono-med text-xs text-[#14B8A6]">TAT 24-48 hrs</span>
              </div>
              <h3 className="text-2xl lg:text-3xl font-bold text-white">Hematopathology</h3>
              <p className="mt-2.5 text-sm text-slate-300 max-w-lg leading-relaxed">
                Bone marrow aspiration & biopsy interpretation, flow cytometry, coagulation studies, peripheral smear and hemoglobin electrophoresis - reported in-house by our CMC Vellore-trained specialist.
              </p>
              <button onClick={() => onBook(["bma", "flow", "ps"])} data-testid="service-book-hemato"
                className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#14B8A6] hover:text-white transition-colors group/btn">
                Book a hematopathology workup <ArrowRight className="w-4 h-4 transition-transform group-hover/btn:translate-x-1" />
              </button>
            </div>
          </motion.div>

          {/* Core pathology */}
          <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}
            className="lg:col-span-5 bg-white/[0.04] backdrop-blur-sm border border-white/10 rounded-[1.75rem] p-8 hover:-translate-y-1.5 hover:bg-white/[0.07] transition-all duration-500" data-testid="service-pathology">
            <div className="w-12 h-12 rounded-2xl bg-[#D92638]/15 flex items-center justify-center">
              <Droplets className="w-6 h-6 text-[#D92638]" />
            </div>
            <h3 className="mt-5 text-xl font-bold text-white">Core Pathology</h3>
            <p className="mt-2.5 text-sm text-slate-400 leading-relaxed">
              Full clinical pathology, biochemistry, microbiology & serology - CBC, thyroid, lipids, liver & kidney panels, dengue/malaria and more, with same-day reporting.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              {["CBC", "HbA1c", "Thyroid", "LFT", "KFT", "Dengue NS1"].map((t) => (
                <span key={t} className="font-mono-med text-[11px] text-slate-300 border border-white/15 rounded-full px-2.5 py-1">{t}</span>
              ))}
            </div>
          </motion.div>

          {/* Radiology */}
          <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}
            className="lg:col-span-4 bg-white/[0.04] backdrop-blur-sm border border-white/10 rounded-[1.75rem] p-8 hover:-translate-y-1.5 hover:bg-white/[0.07] transition-all duration-500" data-testid="service-radiology">
            <div className="w-12 h-12 rounded-2xl bg-[#14B8A6]/15 flex items-center justify-center">
              <ScanLine className="w-6 h-6 text-[#14B8A6]" />
            </div>
            <h3 className="mt-5 text-xl font-bold text-white">Digital Radiology</h3>
            <p className="mt-2.5 text-sm text-slate-400 leading-relaxed">
              Low-dose digital X-ray, ultrasound (USG) by a consultant radiologist, and 12-lead ECG - a credible one-stop diagnostics visit.
            </p>
          </motion.div>

          {/* Home collection */}
          <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}
            className="lg:col-span-4 bg-white/[0.04] backdrop-blur-sm border border-white/10 rounded-[1.75rem] p-8 hover:-translate-y-1.5 hover:bg-white/[0.07] transition-all duration-500" data-testid="service-home-collection">
            <div className="w-12 h-12 rounded-2xl bg-[#D92638]/15 flex items-center justify-center">
              <Home className="w-6 h-6 text-[#D92638]" />
            </div>
            <h3 className="mt-5 text-xl font-bold text-white">Home Sample Collection</h3>
            <p className="mt-2.5 text-sm text-slate-400 leading-relaxed">
              Trained phlebotomists at your doorstep across Vizag with cold-chain sample handling. Book online or on WhatsApp in under a minute.
            </p>
          </motion.div>

          {/* B2B */}
          <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}
            className="lg:col-span-4 bg-white/[0.04] backdrop-blur-sm border border-white/10 rounded-[1.75rem] p-8 hover:-translate-y-1.5 hover:bg-white/[0.07] transition-all duration-500" data-testid="service-b2b">
            <div className="w-12 h-12 rounded-2xl bg-[#14B8A6]/15 flex items-center justify-center">
              <Building2 className="w-6 h-6 text-[#14B8A6]" />
            </div>
            <h3 className="mt-5 text-xl font-bold text-white">B2B Reporting-as-a-Service</h3>
            <p className="mt-2.5 text-sm text-slate-400 leading-relaxed">
              Hospitals & regional labs: send us hematopathology cases and get specialist-signed reports in 24-48 hours. White-label friendly.
            </p>
          </motion.div>

          {/* ECG/activity strip */}
          <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}
            className="lg:col-span-12 relative rounded-[1.75rem] border border-white/10 bg-gradient-to-r from-[#0F2942] to-[#0B192C] p-7 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5 overflow-hidden">
            <div className="flex items-center gap-4">
              <Activity className="w-7 h-7 text-[#D92638]" />
              <div>
                <div className="text-white font-bold text-lg">Every report reviewed by a specialist. Every time.</div>
                <div className="text-slate-400 text-sm mt-0.5">Not technicians. Not algorithms. Doctors.</div>
              </div>
            </div>
            <button onClick={() => onBook([])} data-testid="services-book-button"
              className="shrink-0 flex items-center gap-2 bg-[#D92638] hover:bg-[#B81D2D] text-white text-sm font-semibold px-6 py-3 rounded-full transition-all duration-300 hover:-translate-y-0.5 active:scale-95">
              Book Appointment <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
